import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import moment from 'moment-timezone';
import { buildValidations, validator } from '@eflexsystems/ember-tracked-validations';
import { sortByProp } from 'ramda-adjunct';
import { eflexFeatures } from 'cpc-license-manager/constants/eflex-features';

@buildValidations({
  organization: validator('belongs-to'),
  creator: validator('presence', true),
  stationLimit: [
    validator('presence', true),
    validator('number', {
      integer: true,
      allowString: true,
      gte: 1,
      allowBlank: false,
      allowNone: false,
    }),
  ],
  expirationDate: validator('presence', true),
  label: validator('presence', true),
})
class License extends Model {
  @attr('string') licenseId;
  @attr('string') label;
  @attr('string') jobNumber;
  @attr('number') stationLimit;
  @attr('date', { defaultValue() { return new Date(); } }) expirationDate;
  @attr('boolean', { defaultValue: true }) sendWarnings;
  @attr('objectId') creator;
  @attr('string') notes;

  @attr('date', { defaultValue() { return new Date(); } }) createdAt;
  @attr('date', { defaultValue() { return new Date(); } }) updatedAt;

  @belongsTo('organization', { async: false, inverse: 'licenses' }) organization;
  @hasMany('changelog', { async: false, cascadeDelete: true, inverse: null }) changelogs;
  @hasMany('feature', { async: false, inverse: null, embedded: true }) features;

  get isDirty() {
    return super.isDirty ||
      this.changelogs.some(item => item.isDirty) ||
      this.features.some(item => item.isDirty);
  }

  get level1Features() {
    return this.nonDeletedFeatures?.filter(feature => feature.group === 'level-1');
  }

  get level2Features() {
    return this.nonDeletedFeatures?.filter(feature => feature.group === 'level-2');
  }

  get level3Features() {
    return this.nonDeletedFeatures?.filter(feature => feature.group === 'level-3');
  }

  get addonFeatures() {
    return this.nonDeletedFeatures?.filter(feature => feature.group === 'addon');
  }

  get nonDeletedFeatures() {
    return this.validFeatures?.filter(feature => !feature.isDeleted);
  }

  get validFeatures() {
    const keys = new Set(eflexFeatures.map(el => el.key));
    return this.features.filter((f) => keys.has(f.key));
  }

  get expirationThreshold() {
    return this.organization?.expirationThreshold;
  }

  get daysTillExpired() {
    return moment(this.expirationDate).diff(moment(), 'days') + 1;
  }

  get expirationWarning() {
    if (this.expired) {
      return false;
    }
    return moment(this.expirationDate).subtract(this.expirationThreshold, 'days').isBefore(moment());
  }

  get expired() {
    return moment(this.expirationDate).isBefore(moment());
  }

  get expiredOrWarning() {
    return this.expired || this.expirationWarning;
  }

  get sortedChangelogs() {
    return sortByProp('createdAt', this.changelogs ?? []);
  }

  async save(options) {
    await super.save(options);

    this.changelogs.forEach(changelog => {
      if (!changelog.isDirty) {
        return;
      }

      changelog.save();
    });
  }

  rollbackAttributes() {
    this.features.forEach(feature => {
      if (feature.isDirty) {
        feature.rollbackAttributes();
      }
    });

    this.changelogs.forEach(changelog => {
      if (changelog.isDirty) {
        changelog.rollbackAttributes();
      }
    });

    super.rollbackAttributes(...arguments);
  }
}

export default License;
